/**
 * Shared folder ordering helpers
 *
 * Centralizes how folders are sorted and flattened so the inventory sidebar,
 * forms, and pickers all present folders in the same order and hierarchy.
 */
import type { Folder } from '@/types/card';

import type { FolderSortMode } from '../atoms';

/** Sort a list of sibling folders by the given mode. */
export function sortSiblings(siblings: Folder[], mode: FolderSortMode, countMap: Map<string, number>): Folder[] {
  const sorted = [...siblings];
  switch (mode) {
    case 'name':
      sorted.sort((a, b) => a.name.localeCompare(b.name));
      break;
    case 'cardCount':
      sorted.sort((a, b) => (countMap.get(b.id) ?? 0) - (countMap.get(a.id) ?? 0));
      break;
    case 'updatedAt':
      sorted.sort((a, b) => {
        const aTime = a.updated_at?.seconds ?? 0;
        const bTime = b.updated_at?.seconds ?? 0;
        return bTime - aTime;
      });
      break;
    case 'manual':
    default:
      sorted.sort((a, b) => a.sort_order - b.sort_order);
      break;
  }
  return sorted;
}

export interface FlatFolder {
  folder: Folder;
  /** Nesting depth (0 = root). */
  depth: number;
}

/**
 * Flatten the folder hierarchy into an ordered list with depth tracking,
 * applying the given sort mode at each level.
 */
export function flattenFolders(folders: Folder[], mode: FolderSortMode, countMap: Map<string, number> = new Map()): FlatFolder[] {
  const result: FlatFolder[] = [];

  const addLevel = (parentId: string | null, depth: number) => {
    const siblings = sortSiblings(
      folders.filter((f) => (f.parent_id ?? null) === parentId),
      mode,
      countMap,
    );
    for (const folder of siblings) {
      result.push({ folder, depth });
      addLevel(folder.id, depth + 1);
    }
  };

  addLevel(null, 0);
  return result;
}
